// ŚREDNIA WAŻONA — dzienniczek z kategoriami i wagami + tabela 2-kolumnowa
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

// ===== helpers =====
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }
function parseFraction(raw) {
  if (raw == null) return null;
  const s0 = String(raw).trim();
  if (!s0) return null;
  const s = s0.replace(",", ".");
  if (/^[+-]?\d+$/.test(s)) return [parseInt(s, 10), 1];
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) { const den = parseInt(m[2], 10); if (den === 0) return null; return [parseInt(m[1], 10), den]; }
  if (/^[+-]?(?:\d+|\d*\.\d+)$/.test(s)) {
    const sign = s.startsWith("-") ? -1 : 1;
    const body = s.replace(/^[+-]/, "");
    const [ip, fp=""] = body.split(".");
    const k = fp.length;
    const p = sign * (parseInt(ip || "0", 10) * (10 ** k) + parseInt(fp || "0", 10));
    const q = 10 ** k;
    return [p, q];
  }
  return null;
}

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function sampleGrade(dist) {
  const r = Math.random();
  let acc = 0;
  for (const [grade, prob] of dist) {
    acc += prob;
    if (r <= acc) return grade;
  }
  return dist[dist.length - 1][0];
}
function freqString(grades) {
  const map = {};
  for (const g of grades) map[g] = (map[g] || 0) + 1;
  // pokaż od 5 w dół, pomijając zera
  const order = [5,4,3,2];
  return order.filter(g => map[g] > 0).map(g => `${g}×${map[g]}`).join(", ");
}

// ===== generator =====
export function generateTask() {
  // losuj sensowny zestaw wag
  const weightSets = [
    { sprawdzian: 4, kartkowka: 2, aktywnosc: 1 },
    { sprawdzian: 5, kartkowka: 3, aktywnosc: 1 },
    { sprawdzian: 3, kartkowka: 2, aktywnosc: 1 },
  ];
  const W = pick(weightSets);

  // naturalne liczności: S 1–3, K 2–5, A 4–10
  const cntS = 1 + Math.floor(Math.random() * 3);
  const cntK = 2 + Math.floor(Math.random() * 4);
  const cntA = 4 + Math.floor(Math.random() * 7);

  // rozkłady ocen (bardziej 3–4; aktywność nieco „wyżej”)
  const distS = [[2,0.15],[3,0.35],[4,0.35],[5,0.15]];
  const distK = [[2,0.10],[3,0.40],[4,0.35],[5,0.15]];
  const distA = [[2,0.05],[3,0.35],[4,0.40],[5,0.20]];

  const S = Array.from({length: cntS}, () => sampleGrade(distS));
  const K = Array.from({length: cntK}, () => sampleGrade(distK));
  const A = Array.from({length: cntA}, () => sampleGrade(distA));

  // policz sumy i mianownik (waga * liczność)
  const sumS = S.reduce((a,b)=>a+b,0);
  const sumK = K.reduce((a,b)=>a+b,0);
  const sumA = A.reduce((a,b)=>a+b,0);

  const numerator   = W.sprawdzian*sumS + W.kartkowka*sumK + W.aktywnosc*sumA;
  const denominator = W.sprawdzian*cntS + W.kartkowka*cntK + W.aktywnosc*cntA;
  const mean = numerator / denominator;

  return {
    type: "MeanWeightedDiaryTask",
    data: {
      W, cntS, cntK, cntA, S, K, A,
      sums: {S:sumS, K:sumK, A:sumA},
      numerator, denominator, mean
    },
    uid: `mean_w_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

// ===== treść =====
export function renderTask({ data }) {
  const { W, S, K, A } = data;
  const csvLatex = (arr) => arr.join(",\\ ");

  return (
    <div style={{ textAlign:"left", lineHeight:1.6 }}>
      <div style={{ marginBottom: 6 }}>
        W poniższym dzienniku dla kategorii <b>sprawdziany</b>, <b>kartkówki</b> i <b>aktywność</b> obowiązują następujące
        wagi:&nbsp;
        <span>sprawdziany </span><InlineMath math={`${W.sprawdzian}`} />
        <span>, kartkówki </span><InlineMath math={`${W.kartkowka}`} />
        <span>, aktywność </span><InlineMath math={`${W.aktywnosc}`} /><span>.</span>
      </div>

      <div style={{ margin: "2px 0 6px" }}>
        Uczeń ma następujące oceny z przedmiotu:
      </div>

      <table style={{borderCollapse:"collapse", width:"100%", maxWidth:540, margin:"4px 0"}}>
        <thead>
          <tr>
            <th style={{textAlign:"left", borderBottom:"1px solid #999", padding:"4px 6px"}}>Rodzaj pracy</th>
            <th style={{textAlign:"left", borderBottom:"1px solid #999", padding:"4px 6px"}}>Oceny</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{padding:"4px 6px"}}>Sprawdziany</td>
            <td style={{padding:"4px 6px"}}><InlineMath math={csvLatex(S)} /></td>
          </tr>
          <tr>
            <td style={{padding:"4px 6px"}}>Kartkówki</td>
            <td style={{padding:"4px 6px"}}><InlineMath math={csvLatex(K)} /></td>
          </tr>
          <tr>
            <td style={{padding:"4px 6px"}}>Aktywność</td>
            <td style={{padding:"4px 6px"}}><InlineMath math={csvLatex(A)} /></td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 6 }}>
        Oblicz <b>średnią ważoną ocen</b> <InlineMath math={'\\bar{x}_w'} />.
      </div>
    </div>
  );
}



// ===== input =====
export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3" style={{ marginTop: 6 }}>
      <span><InlineMath math={'\\bar{x}_w = '} /></span>
      <FractionInput value={userAnswer} onChange={onChange} disabled={disabled} label={"\\bar{x}_w"} placeholder="np. 3.85 albo 77/20" />
    </div>
  );
}

// ===== sprawdzanie =====
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || !userAnswer.raw) return { correct:false, message:"Podaj wartość \\(\\bar{x}_w\\)." };
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct:false, message:"Nieprawidłowy zapis (np. 3.75, 15/4)." };

  const uv = parsed[0]/parsed[1];
  const correct = Math.abs(uv - data.mean) <= 0.01 + 1e-12;

  const frac = dfrac(data.numerator, data.denominator);
  const approx = (Math.round(data.mean * 100) / 100).toFixed(2);
  return { correct, expected: `\\bar{x}_w = ${frac} \\;\\approx\\; ${approx}` };
}

// ===== odpowiedź krótka =====
export function renderAnswer({ data }) {
  const approx = (Math.round(data.mean * 100) / 100).toFixed(2);
  const frac = dfrac(data.numerator, data.denominator);
  return <InlineMath math={`\\bar{x}_w = ${frac} \\;\\approx\\; ${approx}`} />;
}

// ===== podgląd odpowiedzi użytkownika =====
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(userAnswer.raw, '\\\\bar{x}_w')} />;
}

// ===== wyjaśnienie =====
export function renderExplanation({ data }) {
  const { W, cntS, cntK, cntA, sums, numerator, denominator, S, K, A, mean } = data;

  const sumSraw = S.join(" + ");
  const sumKraw = K.join(" + ");
  const sumAraw = A.join(" + ");

  const approx = (Math.round(mean * 100) / 100).toFixed(2);

  return (
    <div style={{ textAlign:"left", lineHeight:1.75 }}>
      <div><strong>Krok 1 — wzór z karty wzorów (średnia ważona)</strong></div>
      <BlockMath math={`\\bar{x}_w = \\dfrac{w_1\\cdot a_1 + w_2\\cdot a_2 + \\dots + w_n\\cdot a_n}{w_1 + w_2 + \\dots + w_n}`} />

      <div style={{ marginTop: 8 }}><strong>Krok 2 — podstawiamy dane z dziennika</strong></div>
      <BlockMath math={`S_{\\text{spr}} = ${sumSraw} = ${sums.S}`} />
      <BlockMath math={`S_{\\text{kart}} = ${sumKraw} = ${sums.K}`} />
      <BlockMath math={`S_{\\text{akt}} = ${sumAraw} = ${sums.A}`} />

      <BlockMath math={`\\text{licznik} = w_{\\text{spr}}\\cdot S_{\\text{spr}} + w_{\\text{kart}}\\cdot S_{\\text{kart}} + w_{\\text{akt}}\\cdot S_{\\text{akt}} = ${W.sprawdzian}\\cdot ${sums.S} + ${W.kartkowka}\\cdot ${sums.K} + ${W.aktywnosc}\\cdot ${sums.A} = ${numerator}`} />
      <BlockMath math={`\\text{mianownik} = w_{\\text{spr}}\\cdot n_{\\text{spr}} + w_{\\text{kart}}\\cdot n_{\\text{kart}} + w_{\\text{akt}}\\cdot n_{\\text{akt}} = ${W.sprawdzian}\\cdot ${cntS} + ${W.kartkowka}\\cdot ${cntK} + ${W.aktywnosc}\\cdot ${cntA} = ${denominator}`} />

      <div style={{ marginTop: 8 }}><strong>Krok 3 — obliczamy średnią ważoną</strong></div>
      <BlockMath math={`\\bar{x}_w = \\dfrac{${numerator}}{${denominator}} \\;\\approx\\; ${approx}`} />
    </div>
  );
}
